import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogTrigger } from "@/components/ui/dialog";
import { 
  Camera, ArrowRight, Phone, X, ChevronLeft, ChevronRight
} from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const categoryLabels = {
  birthday: "Birthday Parties",
  wedding: "Weddings",
  party: "General Parties",
  catering: "Catering Setups"
};

const GalleryPage = () => {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [lightboxImage, setLightboxImage] = useState(null);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  useEffect(() => {
    fetchGallery();
  }, []);

  const fetchGallery = async () => {
    try {
      const response = await axios.get(`${API}/gallery`);
      setImages(response.data);
    } catch (error) {
      console.error("Error fetching gallery:", error);
      toast.error("Failed to load gallery");
    } finally {
      setLoading(false);
    }
  };

  const categories = [...new Set(images.map(img => img.category))];
  
  const filteredImages = selectedCategory === "all" 
    ? images 
    : images.filter(img => img.category === selectedCategory);

  const openLightbox = (image, index) => {
    setLightboxImage(image);
    setLightboxIndex(index);
  };

  const closeLightbox = () => {
    setLightboxImage(null);
  };

  const nextImage = () => {
    const nextIndex = (lightboxIndex + 1) % filteredImages.length;
    setLightboxImage(filteredImages[nextIndex]);
    setLightboxIndex(nextIndex);
  };

  const prevImage = () => {
    const prevIndex = (lightboxIndex - 1 + filteredImages.length) % filteredImages.length;
    setLightboxImage(filteredImages[prevIndex]);
    setLightboxIndex(prevIndex);
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-background/80 backdrop-blur-md border-b border-border">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <Camera className="w-10 h-10 text-primary" />
            <span className="font-heading text-2xl font-semibold text-foreground">Gallery</span>
          </Link>
          <div className="flex items-center gap-4">
            <Link to="/rentals">
              <Button variant="outline">View Rentals</Button>
            </Link>
            <Link to="/order">
              <Button className="bg-primary">Order Now</Button>
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-12 bg-gradient-to-br from-pink-50 via-purple-50 to-blue-50">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <p className="text-2xl md:text-3xl font-bold text-foreground mb-2">Sis Carol</p>
          <div className="inline-flex items-center gap-2 bg-white/80 rounded-full px-4 py-2 shadow-sm mb-6">
            <Phone className="w-4 h-4 text-primary" />
            <span className="text-primary font-medium">1876-277-2648</span>
          </div>
          <h1 className="font-heading text-4xl md:text-6xl font-bold text-foreground mb-4">
            Our Party Setups 📸
          </h1>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            See how our rental equipment transforms events! Browse photos from real parties and celebrations.
          </p>
        </div>
      </section>

      {/* Category Filter */}
      <section className="py-6 bg-white border-b sticky top-[73px] z-40">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex flex-wrap gap-2 justify-center">
            <Button
              variant={selectedCategory === "all" ? "default" : "outline"}
              onClick={() => setSelectedCategory("all")}
              className="rounded-full"
            >
              All Photos
            </Button>
            {categories.map(cat => (
              <Button
                key={cat}
                variant={selectedCategory === cat ? "default" : "outline"}
                onClick={() => setSelectedCategory(cat)}
                className="rounded-full"
              >
                {categoryLabels[cat] || cat}
              </Button>
            ))}
          </div>
        </div>
      </section>

      {/* Gallery Grid */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          {loading ? (
            <div className="text-center py-20">
              <div className="animate-spin w-12 h-12 border-4 border-primary border-t-transparent rounded-full mx-auto"></div>
              <p className="mt-4 text-muted-foreground">Loading gallery...</p>
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredImages.map((image, index) => (
                <Card 
                  key={image.id} 
                  className="overflow-hidden group cursor-pointer hover:shadow-xl transition-all duration-300"
                  onClick={() => openLightbox(image, index)}
                  data-testid={`gallery-${image.id}`}
                >
                  <div className="aspect-[4/3] overflow-hidden relative">
                    <img 
                      src={image.image_url} 
                      alt={image.title}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-all duration-300 flex items-center justify-center">
                      <Camera className="w-12 h-12 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                    </div>
                    <Badge className="absolute top-3 left-3 bg-white/90 text-foreground">
                      {categoryLabels[image.category] || image.category}
                    </Badge>
                  </div>
                  <div className="p-4">
                    <h3 className="font-heading text-lg font-semibold">{image.title}</h3>
                    {image.description && (
                      <p className="text-sm text-muted-foreground mt-1">{image.description}</p>
                    )}
                  </div>
                </Card>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Lightbox */}
      {lightboxImage && (
        <div className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center" onClick={closeLightbox}>
          <button 
            className="absolute top-4 right-4 text-white hover:text-gray-300 z-10"
            onClick={closeLightbox}
          >
            <X className="w-8 h-8" />
          </button>
          <button 
            className="absolute left-4 top-1/2 -translate-y-1/2 text-white hover:text-gray-300 bg-black/50 p-2 rounded-full"
            onClick={(e) => { e.stopPropagation(); prevImage(); }}
          >
            <ChevronLeft className="w-8 h-8" />
          </button>
          <button 
            className="absolute right-4 top-1/2 -translate-y-1/2 text-white hover:text-gray-300 bg-black/50 p-2 rounded-full"
            onClick={(e) => { e.stopPropagation(); nextImage(); }}
          >
            <ChevronRight className="w-8 h-8" />
          </button>
          <div className="max-w-4xl max-h-[80vh] p-4" onClick={(e) => e.stopPropagation()}>
            <img 
              src={lightboxImage.image_url} 
              alt={lightboxImage.title}
              className="max-w-full max-h-[70vh] object-contain mx-auto rounded-lg"
            />
            <div className="text-center mt-4 text-white">
              <h3 className="font-heading text-xl font-semibold">{lightboxImage.title}</h3>
              {lightboxImage.description && (
                <p className="text-gray-300 mt-1">{lightboxImage.description}</p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-r from-pink-500 to-purple-500 text-white">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-bold mb-4">
            Want This for Your Event? 🎉
          </h2>
          <p className="text-lg text-white/90 mb-8">
            Book our rental equipment and create your own amazing party setup!
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/rentals">
              <Button className="bg-white text-purple-600 hover:bg-purple-50 rounded-full px-8 py-6 text-lg font-bold">
                Browse Rentals <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
            <Link to="/rental-packages">
              <Button variant="outline" className="border-white text-white hover:bg-white/20 rounded-full px-8 py-6 text-lg font-bold">
                View Package Deals
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 bg-foreground text-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <p className="text-white/70">
            © 2026 Sis Carol Cakes & Party Rentals | Irie Eatz Restaurant, Market Street, Ocho Rios
          </p>
          <p className="text-white/70 mt-2">
            <Phone className="w-4 h-4 inline mr-1" /> 1876-277-2648
          </p>
        </div>
      </footer>
    </div>
  );
};

export default GalleryPage;
